import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { useFormContext, useWatch, type UseFieldArrayReturn } from 'react-hook-form'
import type {
  AllergyItem,
  ObservationItem,
  ProgressNoteEditFormValues,
} from '../progressNoteEditSchema'
import { VITAL_UNIT_OPTIONS, canonicalVitalCode, defaultVitalUnit } from '../vitalUnits'
import { QR_VITAL_LINKIDS } from '../progressNoteQrHydrate'
import {
  BP_DIASTOLIC_LOINC,
  BP_SYSTOLIC_LOINC,
  buildVitalReadRows,
  printableUnit,
} from './vitalReadRows'
import { MONO_FONT, SUBSECTION_LABEL_COLOR } from '../tokens'
import { NoteHeading } from './NoteHeading'
import { LOW_CONFIDENCE_REASON } from '../scribeDraftPayload'
import {
  PHYSICAL_EXAM_HEADING,
  TELEHEALTH_LIMITATIONS_SUBHEADING,
  TELEHEALTH_LIMITATIONS_TEXT,
} from '../progressNoteText'

// Allergen classes (matches the chart's manual add-allergy modal); drives the
// Allergies tab's Drug vs Non-Drug column.
const ALLERGY_CATEGORY_OPTIONS = [
  { value: 'medication', label: 'Medication' },
  { value: 'food', label: 'Food' },
  { value: 'environment', label: 'Environment' },
  { value: 'biologic', label: 'Biologic' },
]

// Vitals the AI can generate (mirrors the draft-prompt vitals list) — the
// row picker offers exactly this set.
const AI_GENERATABLE_VITALS = new Set([
  '8480-6', // BP systolic
  '8462-4', // BP diastolic
  '8867-4', // heart rate
  '9279-1', // respiratory rate
  '8310-5', // body temperature
  '29463-7', // body weight
  '8302-2', // body height
  '59408-5', // O2 saturation
  '39156-5', // BMI
  '2339-0', // blood glucose
  '72514-3', // pain severity (0–10)
])

const PATIENT_REPORTED_PREFIX = 'Patient-reported home reading'

// A vital's home-reading flag and its free-text comment share the single
// `note` string (no structured DTO field). Home-reading is the canonical
// prefix; any provider comment follows it after a separator. Parse/compose
// keep the two independent so toggling the checkbox never destroys a typed
// comment and vice-versa (PD-1146). Matching on the prefix (not a substring)
// avoids a plain comment that merely mentions "patient-reported" self-ticking.
function parseVitalNote(note: string | null | undefined): {
  homeReading: boolean
  comment: string
} {
  const raw = (note ?? '').trim()
  if (!raw.toLowerCase().startsWith(PATIENT_REPORTED_PREFIX.toLowerCase())) {
    return { homeReading: false, comment: raw }
  }
  const comment = raw
    .slice(PATIENT_REPORTED_PREFIX.length)
    .replace(/^[\s—:.-]+/, '')
    .trim()
  return { homeReading: true, comment }
}

function composeVitalNote(homeReading: boolean, comment: string): string | null {
  const c = comment.trim()
  if (homeReading && c) return `${PATIENT_REPORTED_PREFIX} — ${c}`
  if (homeReading) return PATIENT_REPORTED_PREFIX
  return c || null
}

// "Updated" marker on a chart allergy whose reaction/status changed during
// this encounter — shared by the edit + view allergy tables.
function UpdatedBadge() {
  return (
    <Tooltip
      label="Reaction or status updated during this encounter. The chart record is not changed."
      multiline
      w={240}
      withArrow
    >
      <Badge size="xs" color="blue" variant="light">
        Updated
      </Badge>
    </Tooltip>
  )
}

export interface ObjectiveSectionProps {
  readOnly: boolean
  /** Optional — when provided, edit mode renders `+ Add Row` + remove icons. */
  observationsField?: UseFieldArrayReturn<ProgressNoteEditFormValues, 'observations'>
  /** Optional — when provided, edit mode renders the editable allergy table (PD-1141). */
  allergiesField?: UseFieldArrayReturn<ProgressNoteEditFormValues, 'allergies'>
}

export function ObjectiveSection({
  readOnly,
  observationsField,
  allergiesField,
}: ObjectiveSectionProps) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()
  const observations = useWatch({ control, name: 'observations' })
  const allergies = useWatch({ control, name: 'allergies' })

  const vitals = (observations || []).filter((o) => o.category === 'vital-signs')
  // Slot the fused Blood-pressure row occupies: the first BP component in row order.
  const bpAnchorId = vitals.find(
    (v) => v.code === BP_SYSTOLIC_LOINC || v.code === BP_DIASTOLIC_LOINC,
  )?.id
  // Exam prose + uncoded AI observations — rendered in the Examination block below the vitals table.
  const examObservations = (observations || []).filter((o) => o.category !== 'vital-signs')
  // A label-less row is free prose (editable textarea); a coded/labelled row is
  // a structured observation shown read-only — matches draftToSoap's raw-emit rule.
  const isNarrativeExam = (o: ObservationItem) => !o.code && !o.display
  const hasNarrativeRow = examObservations.some(isNarrativeExam)
  // Always shown in edit mode (so the read-only sentence is visible even when empty); in read mode only when non-empty.
  const showAllergies = !readOnly || (allergies && allergies.length > 0)

  // Unit chosen on a row that has no value yet (the unit Select is usable
  // before the value is typed) — consumed when the first value lands.
  const [pendingUnits, setPendingUnits] = useState<Record<string, string>>({})

  const handleVitalValueChange = (id: string, value: string) => {
    const idx = (observations || []).findIndex((o) => o.id === id)
    if (idx < 0) return
    const parsed = value === '' ? null : Number(value)
    setValue(
      `observations.${idx}.valueQuantity`,
      parsed != null && !Number.isNaN(parsed)
        ? {
            value: parsed,
            // Priority: unit already on the row → unit picked pre-value →
            // dropdown default → the vital's fixed unit.
            unit:
              observations[idx].valueQuantity?.unit ??
              pendingUnits[id] ??
              defaultVitalUnit(canonicalVitalCode(observations[idx].code)) ??
              QR_VITAL_LINKIDS.find((v) => v.loinc === canonicalVitalCode(observations[idx].code))
                ?.unit ??
              '',
          }
        : null,
      { shouldDirty: true },
    )
    // Mirror the value as a string for non-numeric vitals if needed. Also
    // stash the row's unit so clear-then-retype doesn't revert to the default.
    if (parsed == null || Number.isNaN(parsed)) {
      const unit = observations[idx].valueQuantity?.unit
      if (unit) setPendingUnits((prev) => ({ ...prev, [id]: unit }))
      setValue(`observations.${idx}.valueString`, value || null, { shouldDirty: true })
    }
    // PD-1149 — editing a flagged vital counts as reviewing it: clear the flag.
    if (observations[idx].needsReview) {
      setValue(`observations.${idx}.needsReview`, false, { shouldDirty: true })
    }
  }

  // PD-1149 — "Confirm value" resolves a flagged vital as-is (a legit extreme,
  // e.g. athlete bradycardia), so Lock unblocks without altering a true value.
  const handleConfirmVital = (ids: string[]) => {
    for (const id of ids) {
      const idx = (observations || []).findIndex((o) => o.id === id)
      if (idx >= 0) setValue(`observations.${idx}.needsReview`, false, { shouldDirty: true })
    }
  }

  // PD-1149 — red "Needs review" pill on an implausible vital, with a
  // "Confirm value" action in edit mode (read-only views show the pill only).
  // PD-1600 — the same pill carries the AI's low-confidence reason when it has one.
  const renderReviewFlag = (needsReview: boolean | undefined, ids: string[], reason?: string) => {
    if (!needsReview) return null
    return (
      <Group gap={6} mt={6} wrap="nowrap" align="center">
        <Tooltip
          label={reason ?? 'Outside the expected range — confirm or correct before locking.'}
          multiline
          w={240}
          withArrow
        >
          <Badge size="xs" color="red" variant="light" style={{ cursor: 'default' }}>
            Needs review
          </Badge>
        </Tooltip>
        {!readOnly && ids.length > 0 && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={() => handleConfirmVital(ids)}
          >
            Confirm value
          </Button>
        )}
      </Group>
    )
  }

  // PD-1600 — a low-confidence AI allergy row: confirming clears the flag as-is.
  const renderAllergyFlag = (allergy: AllergyItem, idx: number) => {
    if (!allergy.needsReview) return null
    return (
      <Group gap={6} mt={4} wrap="nowrap" align="center">
        <Tooltip label={allergy.reviewReason ?? LOW_CONFIDENCE_REASON} multiline w={240} withArrow>
          <Badge size="xs" color="red" variant="light" style={{ cursor: 'default' }}>
            Needs review
          </Badge>
        </Tooltip>
        {!readOnly && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={() => setValue(`allergies.${idx}.needsReview`, false, { shouldDirty: true })}
          >
            Confirm
          </Button>
        )}
      </Group>
    )
  }

  const handleVitalLabelChange = (id: string, value: string) => {
    const idx = (observations || []).findIndex((o) => o.id === id)
    if (idx < 0) return
    setValue(`observations.${idx}.display`, value, { shouldDirty: true })
  }

  const handleVitalUnitChange = (id: string, unit: string | null) => {
    if (!unit) return
    const idx = (observations || []).findIndex((o) => o.id === id)
    const quantity = idx >= 0 ? observations[idx].valueQuantity : null
    if (quantity == null) {
      setPendingUnits((prev) => ({ ...prev, [id]: unit }))
      return
    }
    setValue(`observations.${idx}.valueQuantity`, { ...quantity, unit }, { shouldDirty: true })
  }

  // The vital picker always offers the full AI-generatable set (draft-prompt
  // vitals list); vitals already on another row are shown disabled, not hidden.
  const vitalPickerOptions = (rowId: string, currentCode: string) =>
    QR_VITAL_LINKIDS.filter((v) => AI_GENERATABLE_VITALS.has(v.loinc)).map((v) => ({
      value: v.loinc,
      label: v.display,
      disabled:
        v.loinc !== currentCode &&
        (observations || []).some(
          (o) => o.code && canonicalVitalCode(o.code) === v.loinc && o.id !== rowId,
        ),
    }))

  const handleVitalPick = (id: string, loinc: string | null) => {
    if (!loinc) return
    const idx = (observations || []).findIndex((o) => o.id === id)
    const std = QR_VITAL_LINKIDS.find((v) => v.loinc === loinc)
    if (idx < 0 || !std) return
    setValue(`observations.${idx}.code`, loinc, { shouldDirty: true })
    setValue(`observations.${idx}.display`, std.display, { shouldDirty: true })
    // Switching vitals resets the unit to the new vital's default — carrying
    // the previous vital's unit (e.g. lb onto a temperature) would be wrong.
    const quantity = observations[idx].valueQuantity
    if (quantity != null) {
      setValue(
        `observations.${idx}.valueQuantity`,
        { value: quantity.value, unit: defaultVitalUnit(loinc) ?? std.unit },
        { shouldDirty: true },
      )
    }
    setPendingUnits((prev) => {
      const { [id]: _dropped, ...rest } = prev
      return rest
    })
  }

  // Recompose a vital's `note` from its CURRENT parsed value so the checkbox
  // and the comment field never clobber each other. BP systolic + diastolic
  // share the single `bpNote` slot, so both rows are written together.
  const updateVitalNote = (
    id: string,
    next: (current: { homeReading: boolean; comment: string }) => string | null,
  ) => {
    const all = observations || []
    const row = all.find((o) => o.id === id)
    if (!row) return
    const nextNote = next(parseVitalNote(row.note))
    const isBp = (c: string) => c === BP_SYSTOLIC_LOINC || c === BP_DIASTOLIC_LOINC
    const targets = row.code && isBp(row.code) ? all.filter((o) => o.code && isBp(o.code)) : [row]
    for (const target of targets) {
      const idx = all.findIndex((o) => o.id === target.id)
      if (idx < 0) continue
      setValue(`observations.${idx}.note`, nextNote, { shouldDirty: true })
    }
  }

  // BP is two observations (systolic 8480-6 + diastolic 8462-4). Edit mode
  // fuses them into one "Blood pressure" row — two inline inputs, each writing
  // its own component; one shared home-reading checkbox + comment (updateVitalNote
  // dual-writes the note to both); one remove that drops both. Rendered once at
  // bpAnchorId when at least one component is present; a missing component shows
  // an empty disabled box (never crashes, never drops the present one).
  const renderBloodPressureEditRow = () => {
    const all = observations || []
    const sysIdx = all.findIndex((o) => o.code === BP_SYSTOLIC_LOINC)
    const diaIdx = all.findIndex((o) => o.code === BP_DIASTOLIC_LOINC)
    const sys = sysIdx >= 0 ? all[sysIdx] : undefined
    const dia = diaIdx >= 0 ? all[diaIdx] : undefined
    const noteAnchor = (sys ?? dia)!
    const { homeReading, comment } = parseVitalNote(noteAnchor.note)
    const bpValue = (o: ObservationItem | undefined) =>
      o?.valueQuantity != null ? String(o.valueQuantity.value) : (o?.valueString ?? '')
    return (
      <Table.Tr key="blood-pressure" data-testid="vital-row-blood-pressure">
        <Table.Td style={{ width: 220, verticalAlign: 'top' }}>
          <Text size="sm" fw={600} c={SUBSECTION_LABEL_COLOR}>
            Blood pressure
          </Text>
        </Table.Td>
        <Table.Td style={{ verticalAlign: 'top' }}>
          <Group gap={6} wrap="nowrap">
            <TextInput
              size="xs"
              w={64}
              value={bpValue(sys)}
              disabled={!sys}
              onChange={(e) => {
                if (sys) handleVitalValueChange(sys.id, e.currentTarget.value)
              }}
              placeholder="sys"
              data-testid="vital-value-8480-6"
            />
            <Text size="sm">/</Text>
            <TextInput
              size="xs"
              w={64}
              value={bpValue(dia)}
              disabled={!dia}
              onChange={(e) => {
                if (dia) handleVitalValueChange(dia.id, e.currentTarget.value)
              }}
              placeholder="dia"
              data-testid="vital-value-8462-4"
            />
            <Text size="xs" c="dimmed">
              mmHg
            </Text>
          </Group>
          <Checkbox
            size="xs"
            mt={6}
            label={PATIENT_REPORTED_PREFIX}
            checked={homeReading}
            onChange={(e) =>
              updateVitalNote(noteAnchor.id, (cur) =>
                composeVitalNote(e.currentTarget.checked, cur.comment),
              )
            }
            data-testid="vital-patient-reported-blood-pressure"
          />
          <TextInput
            size="xs"
            mt={4}
            value={comment}
            placeholder="Comment (optional)"
            onChange={(e) =>
              updateVitalNote(noteAnchor.id, (cur) =>
                composeVitalNote(cur.homeReading, e.currentTarget.value),
              )
            }
            data-testid="vital-comment-blood-pressure"
          />
          {renderReviewFlag(
            Boolean(sys?.needsReview || dia?.needsReview),
            [sys?.id, dia?.id].filter((id): id is string => Boolean(id)),
          )}
        </Table.Td>
        {observationsField && (
          <Table.Td style={{ width: 40, verticalAlign: 'top' }}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => observationsField.remove([sysIdx, diaIdx].filter((i) => i >= 0))}
              aria-label="Remove blood pressure"
            >
              <IconX size={14} />
            </ActionIcon>
          </Table.Td>
        )}
      </Table.Tr>
    )
  }

  // First allergy's SNOMED code, shown inline beside the comma-joined displays.
  const firstSnomedCode = (allergies ?? []).find((a) => {
    if (!a.code) return false
    if (!a.system) return true // assume SNOMED when system is not stamped
    return a.system.toLowerCase().includes('snomed')
  })?.code

  return (
    <Box mb="xl">
      <NoteHeading level="section" id="objective" label="Objective" />

      <Stack gap="md">
        <Box>
          <NoteHeading level="sub" id="vitals" label="Vitals" />
          {vitals.length === 0 ? (
            <Text size="xs" c="dimmed">
              No vitals captured.
            </Text>
          ) : readOnly ? (
            // READ mode: BP systolic + diastolic are fused into one row.
            <Table withTableBorder withColumnBorders striped="even">
              <Table.Tbody>
                {buildVitalReadRows(vitals).map((row) => (
                  <Table.Tr key={row.key} data-testid={`vital-row-${row.key}`}>
                    <Table.Td style={{ width: 220, verticalAlign: 'top' }}>
                      <Text size="sm" fw={600} c={SUBSECTION_LABEL_COLOR}>
                        {row.label}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: 'top' }}>
                      <Text size="sm" style={{ fontFamily: MONO_FONT }}>
                        {row.value}
                      </Text>
                      {row.note && (
                        <Text size="xs" c="dimmed" fs="italic" mt={2}>
                          {row.note}
                        </Text>
                      )}
                      {renderReviewFlag(row.needsReview, [])}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            // EDIT mode: systolic + diastolic render as one fused Blood-pressure
            // row (two inputs, shared note); every other vital is one row.
            <Table withTableBorder withColumnBorders striped="even">
              <Table.Tbody>
                {vitals.map((vital) => {
                  // BP: render the fused row once at the anchor slot; drop the other component.
                  if (vital.code === BP_SYSTOLIC_LOINC || vital.code === BP_DIASTOLIC_LOINC) {
                    return vital.id === bpAnchorId ? renderBloodPressureEditRow() : null
                  }
                  const editValue =
                    vital.valueQuantity != null
                      ? String(vital.valueQuantity.value)
                      : (vital.valueString ?? '')
                  // Absolute index in `observations[]` — vitals[] is a filtered slice, so remove() must splice the real row.
                  const absoluteIdx = (observations || []).findIndex((o) => o.id === vital.id)
                  const rowCode = vital.code ? canonicalVitalCode(vital.code) : vital.code
                  const unitOptions = rowCode ? VITAL_UNIT_OPTIONS[rowCode] : undefined
                  const currentUnit = vital.valueQuantity?.unit
                  const standard = QR_VITAL_LINKIDS.find((v) => v.loinc === rowCode)
                  // Fixed-unit vitals show their unit in the same right column
                  // as the unit dropdowns (aligned, outside the input); a
                  // `{score}`-style annotation unit renders as no unit.
                  const fixedUnit = unitOptions
                    ? undefined
                    : printableUnit(currentUnit || standard?.unit) || undefined
                  // AI-generatable vitals stay re-pickable from the dropdown;
                  // other rows (hc, legacy free text) keep a name text box
                  // — the picker has no option for them and would render blank.
                  // Blank rows must pick (free-text vitals aren't allowed).
                  const pickable = Boolean(rowCode && AI_GENERATABLE_VITALS.has(rowCode))
                  const showPicker = pickable || (!vital.code && !vital.display)
                  const valueDisabled = !vital.code && !vital.display
                  // BP rows share one bpNote — render the checkbox on systolic only.
                  const hasNoteSlot =
                    Boolean(standard?.noteLinkId) && rowCode !== BP_DIASTOLIC_LOINC
                  const { homeReading: isPatientReported, comment: vitalComment } = parseVitalNote(
                    vital.note,
                  )
                  return (
                    <Table.Tr key={vital.id} data-testid={`vital-row-${vital.code || vital.id}`}>
                      <Table.Td style={{ width: 220, verticalAlign: 'top' }}>
                        {showPicker ? (
                          <Select
                            size="xs"
                            data={vitalPickerOptions(vital.id, rowCode)}
                            value={rowCode || null}
                            placeholder="Select vital"
                            searchable
                            onChange={(loinc) => handleVitalPick(vital.id, loinc)}
                            data-testid="vital-picker"
                          />
                        ) : (
                          <TextInput
                            size="xs"
                            value={vital.display}
                            onChange={(e) =>
                              handleVitalLabelChange(vital.id, e.currentTarget.value)
                            }
                            placeholder="e.g. Blood Pressure"
                          />
                        )}
                      </Table.Td>
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        <Group gap={6} wrap="nowrap">
                          <TextInput
                            size="xs"
                            style={{ flex: 1 }}
                            value={editValue}
                            disabled={valueDisabled}
                            onChange={(e) =>
                              handleVitalValueChange(vital.id, e.currentTarget.value)
                            }
                            placeholder="value"
                          />
                          {unitOptions ? (
                            <Select
                              size="xs"
                              w={96}
                              data={unitOptions.map((o) => o.display)}
                              value={
                                unitOptions.some((o) => o.display === currentUnit)
                                  ? currentUnit
                                  : unitOptions.some((o) => o.display === pendingUnits[vital.id])
                                    ? pendingUnits[vital.id]
                                    : defaultVitalUnit(rowCode)
                              }
                              allowDeselect={false}
                              onChange={(unit) => handleVitalUnitChange(vital.id, unit)}
                              comboboxProps={{ width: 110, position: 'bottom-end' }}
                              data-testid={`vital-unit-${vital.code}`}
                            />
                          ) : fixedUnit ? (
                            <Text size="xs" c="dimmed" w={96} ta="center" style={{ flexShrink: 0 }}>
                              {fixedUnit}
                            </Text>
                          ) : null}
                        </Group>
                        {hasNoteSlot && (
                          <>
                            <Checkbox
                              size="xs"
                              mt={6}
                              label={PATIENT_REPORTED_PREFIX}
                              checked={isPatientReported}
                              onChange={(e) =>
                                updateVitalNote(vital.id, (cur) =>
                                  composeVitalNote(e.currentTarget.checked, cur.comment),
                                )
                              }
                              data-testid={`vital-patient-reported-${vital.code}`}
                            />
                            <TextInput
                              size="xs"
                              mt={4}
                              value={vitalComment}
                              placeholder="Comment (optional)"
                              onChange={(e) =>
                                updateVitalNote(vital.id, (cur) =>
                                  composeVitalNote(cur.homeReading, e.currentTarget.value),
                                )
                              }
                              data-testid={`vital-comment-${vital.code}`}
                            />
                          </>
                        )}
                        {/* Vitals with no note slot (BMI, legacy/free-text) still
                            surface an existing comment in edit mode — read-only. */}
                        {!hasNoteSlot && vital.note && (
                          <Text size="xs" c="dimmed" fs="italic" mt={4}>
                            {vital.note}
                          </Text>
                        )}
                        {renderReviewFlag(vital.needsReview, [vital.id], vital.reviewReason)}
                      </Table.Td>
                      {observationsField && (
                        <Table.Td style={{ width: 40, verticalAlign: 'top' }}>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => {
                              if (absoluteIdx >= 0) observationsField.remove(absoluteIdx)
                            }}
                            aria-label="Remove vital"
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}
          {!readOnly && observationsField && (
            <Button
              size="xs"
              variant="light"
              color="blue"
              mt="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                observationsField.append({
                  id: crypto.randomUUID(),
                  code: '',
                  display: '',
                  category: 'vital-signs',
                  valueQuantity: null,
                  valueString: null,
                  note: null,
                })
              }
              data-testid="objective-add-vital-row"
            >
              Add Row
            </Button>
          )}
        </Box>

        {/* Physical Examination — always rendered: the fixed telehealth
            limitations block (PD-1316) + the doctor's objective prose and
            text-valued AI observations (editable); numeric rows read-only. */}
        <Box data-testid="objective-examination">
          <NoteHeading level="sub" id="physical-exam" label={PHYSICAL_EXAM_HEADING} />
          <Text size="xs" fw={700} c={SUBSECTION_LABEL_COLOR} mb={6}>
            {TELEHEALTH_LIMITATIONS_SUBHEADING}
          </Text>
          <Box
            mb="sm"
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            <Text size="sm" c="dark.7" style={{ lineHeight: 1.7 }}>
              {TELEHEALTH_LIMITATIONS_TEXT}
            </Text>
          </Box>
          {examObservations.length === 0 ? (
            !readOnly && (
              <Text size="xs" c="dimmed">
                No examination notes.
              </Text>
            )
          ) : (
            <Stack gap="xs">
              {examObservations.map((obs) => {
                // Text-valued rows are editable whether labelled or not
                // (PD-1516: AI-dictated exam findings arrive labelled and
                // must be editable BEFORE the first save — the post-save
                // hydrate flattens them to the narrative row anyway). Only
                // numeric rows stay read-only: their unit belongs to the
                // vitals grid, not a free-text edit.
                if (readOnly || !observationsField || obs.valueQuantity != null) {
                  return (
                    <Text
                      key={obs.id}
                      size="sm"
                      c="dark.7"
                      style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                    >
                      {obs.display && (
                        <Text component="span" fw={600}>
                          {obs.display}:{' '}
                        </Text>
                      )}
                      {obs.valueQuantity != null
                        ? `${obs.valueQuantity.value} ${obs.valueQuantity.unit}`.trim()
                        : obs.valueString || '—'}
                      {obs.note && (
                        <Text component="span" c="dimmed" fs="italic">
                          {' '}
                          ({obs.note})
                        </Text>
                      )}
                    </Text>
                  )
                }
                const absoluteIdx = (observations || []).findIndex((o) => o.id === obs.id)
                return (
                  <Box key={obs.id} style={{ position: 'relative' }}>
                    {obs.display && (
                      <Text size="xs" fw={600} c="dark.7" mb={4}>
                        {obs.display}
                      </Text>
                    )}
                    <Textarea
                      autosize
                      minRows={obs.display ? 1 : 3}
                      placeholder="Examination findings"
                      value={obs.valueString ?? ''}
                      onChange={(e) => {
                        if (absoluteIdx < 0) return
                        setValue(
                          `observations.${absoluteIdx}.valueString`,
                          e.currentTarget.value || null,
                          { shouldDirty: true },
                        )
                      }}
                      data-testid={`objective-exam-narrative-${obs.id}`}
                      styles={{ input: { paddingRight: 36 } }}
                    />
                    {obs.note && (
                      <Text size="xs" c="dimmed" fs="italic" mt={2}>
                        {obs.note}
                      </Text>
                    )}
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Remove examination note"
                      onClick={() => {
                        if (absoluteIdx >= 0) observationsField.remove(absoluteIdx)
                      }}
                      style={{ position: 'absolute', top: 6, right: 6 }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Box>
                )
              })}
            </Stack>
          )}
          {!readOnly && observationsField && !hasNarrativeRow && (
            <Button
              size="xs"
              variant="light"
              color="blue"
              mt="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                observationsField.append({
                  id: crypto.randomUUID(),
                  code: '',
                  display: '',
                  category: 'exam',
                  valueQuantity: null,
                  valueString: null,
                  note: null,
                })
              }
              data-testid="objective-add-exam-note"
            >
              Add Examination Note
            </Button>
          )}
        </Box>

        {/* Allergies — editable table in edit mode (PD-1141: rows added in this
            note are editable; rows carried from the chart are read-only and
            tagged "Chart"); read-only table in view/locked mode (the read view
            threads no allergiesField). */}
        {!readOnly && allergiesField ? (
          <Box data-testid="allergies-editor">
            <NoteHeading level="sub" id="allergies" label="Allergies">
              <Text
                component="span"
                c="dimmed"
                size="xs"
                ml={6}
                style={{ fontFamily: MONO_FONT, letterSpacing: 0 }}
              >
                LOINC 48765-2
              </Text>
            </NoteHeading>
            {!allergies || allergies.length === 0 ? (
              <Text size="xs" c="dimmed">
                No known allergies.
              </Text>
            ) : (
              <Table withTableBorder withColumnBorders striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Allergy</Table.Th>
                    <Table.Th>Reaction</Table.Th>
                    <Table.Th style={{ width: 150 }}>Category</Table.Th>
                    <Table.Th style={{ width: 130 }}>Status</Table.Th>
                    <Table.Th style={{ width: 40 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allergies.map((allergy, idx) =>
                    allergy.external ? (
                      // Chart rows (PD-1141): the allergen identity (name/code,
                      // category) stays read-only — changing it would be a
                      // different allergy = a new row. Reaction + status are
                      // editable within the note; edits flag `updated` (badge
                      // only — the chart record is never written).
                      <Table.Tr key={allergy.id} data-testid={`allergy-row-external-${idx}`}>
                        <Table.Td>
                          <Group gap={8} wrap="nowrap">
                            <Text size="sm">{allergy.display || allergy.code || '—'}</Text>
                            <Badge size="xs" color="gray" variant="light">
                              Chart
                            </Badge>
                            {allergy.updated && <UpdatedBadge />}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={allergy.reaction ?? ''}
                            onChange={(e) => {
                              setValue(`allergies.${idx}.reaction`, e.currentTarget.value || null, {
                                shouldDirty: true,
                              })
                              setValue(`allergies.${idx}.updated`, true, { shouldDirty: true })
                            }}
                            placeholder="e.g. Hives"
                            data-testid={`allergy-external-reaction-${idx}`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {allergy.category || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Select
                            size="xs"
                            data={[
                              { value: 'active', label: 'Active' },
                              { value: 'inactive', label: 'Inactive' },
                              { value: 'resolved', label: 'Resolved' },
                            ]}
                            value={allergy.clinicalStatus}
                            allowDeselect={false}
                            onChange={(status) => {
                              if (!status) return
                              setValue(
                                `allergies.${idx}.clinicalStatus`,
                                status as 'active' | 'inactive' | 'resolved',
                                { shouldDirty: true },
                              )
                              setValue(`allergies.${idx}.updated`, true, { shouldDirty: true })
                            }}
                            data-testid={`allergy-external-status-${idx}`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Tooltip
                            label="This will not remove the allergy from the patient chart."
                            multiline
                            w={220}
                            withArrow
                          >
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              aria-label="Remove from note"
                              onClick={() => allergiesField.remove(idx)}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      <Table.Tr key={allergy.id} data-testid={`allergy-row-${idx}`}>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={allergy.display}
                            onChange={(e) => {
                              setValue(`allergies.${idx}.display`, e.currentTarget.value, {
                                shouldDirty: true,
                              })
                              // Editing a flagged row counts as reviewing it.
                              if (allergy.needsReview) {
                                setValue(`allergies.${idx}.needsReview`, false, {
                                  shouldDirty: true,
                                })
                              }
                            }}
                            placeholder="e.g. Penicillin"
                            data-testid={`allergy-display-${idx}`}
                          />
                          {renderAllergyFlag(allergy, idx)}
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={allergy.reaction ?? ''}
                            onChange={(e) =>
                              setValue(`allergies.${idx}.reaction`, e.currentTarget.value || null, {
                                shouldDirty: true,
                              })
                            }
                            placeholder="e.g. Hives"
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            size="xs"
                            data={ALLERGY_CATEGORY_OPTIONS}
                            value={allergy.category}
                            clearable
                            placeholder="—"
                            onChange={(category) =>
                              setValue(
                                `allergies.${idx}.category`,
                                category as AllergyItem['category'],
                                { shouldDirty: true },
                              )
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            size="xs"
                            data={[
                              { value: 'active', label: 'Active' },
                              { value: 'inactive', label: 'Inactive' },
                              { value: 'resolved', label: 'Resolved' },
                            ]}
                            value={allergy.clinicalStatus}
                            allowDeselect={false}
                            onChange={(status) => {
                              if (!status) return
                              setValue(
                                `allergies.${idx}.clinicalStatus`,
                                status as 'active' | 'inactive' | 'resolved',
                                { shouldDirty: true },
                              )
                            }}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => allergiesField.remove(idx)}
                            aria-label="Remove allergy"
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ),
                  )}
                </Table.Tbody>
              </Table>
            )}
            <Button
              size="xs"
              variant="light"
              color="blue"
              mt="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                allergiesField.append({
                  id: crypto.randomUUID(),
                  system: null,
                  code: null,
                  display: '',
                  clinicalStatus: 'active',
                  reaction: null,
                  severity: null,
                  category: null,
                  external: false,
                  chartId: null,
                })
              }
              data-testid="objective-add-allergy"
            >
              Add Allergy
            </Button>
          </Box>
        ) : (
          showAllergies && (
            <Box data-testid="allergies-view">
              <NoteHeading level="sub" id="allergies" label="Allergies">
                <Text
                  component="span"
                  c="dimmed"
                  size="xs"
                  ml={6}
                  style={{ fontFamily: MONO_FONT, letterSpacing: 0 }}
                >
                  LOINC 48765-2
                </Text>
              </NoteHeading>
              {!allergies || allergies.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No known allergies.
                </Text>
              ) : (
                <Table withTableBorder withColumnBorders striped="even">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Allergy</Table.Th>
                      <Table.Th>Reaction</Table.Th>
                      <Table.Th style={{ width: 150 }}>Category</Table.Th>
                      <Table.Th style={{ width: 130 }}>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {allergies.map((allergy, idx) => (
                      <Table.Tr key={allergy.id} data-testid={`allergy-view-row-${idx}`}>
                        <Table.Td>
                          <Group gap={8} wrap="nowrap">
                            <Text size="sm">{allergy.display || allergy.code || '—'}</Text>
                            {allergy.external && (
                              <Badge size="xs" color="gray" variant="light">
                                Chart
                              </Badge>
                            )}
                            {allergy.updated && <UpdatedBadge />}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {allergy.reaction || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {allergy.category || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {allergy.clinicalStatus}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              {firstSnomedCode && (
                <Text size="xs" c="dimmed" mt={6}>
                  SNOMED {firstSnomedCode}
                </Text>
              )}
            </Box>
          )
        )}
      </Stack>
    </Box>
  )
}
